
import { TestResponse, RecruitmentStatus } from "@/types/test-response";
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { Pencil, History, Trash2, Mail } from "lucide-react";
import { format } from "date-fns";
import { StatusDisplay } from "./StatusDisplay";
import { ScoreDisplay } from "./ScoreDisplay";
import { IntegrityDisplay } from "./IntegrityDisplay";
import { RecruitmentStatusSelect } from "./RecruitmentStatusSelect";
import { ChangeHistoryDrawer } from "./ChangeHistoryDrawer";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useRecruitmentStatus } from "@/hooks/useRecruitmentStatus";
import { RecruitmentNotesDialog } from "./RecruitmentNotesDialog";
import { DeleteResponseDialog } from "../DeleteResponseDialog";
import { useDeleteResponse } from "@/hooks/useDeleteResponse";
import { useInterviewInvitation } from "@/hooks/useInterviewInvitation";


interface ResponseTableRowProps {
  response: TestResponse;
  canManageRecruitment?: boolean;
  canDelete?: boolean;
  isSelected?: boolean;
  onSelectionChange?: (selected: boolean) => void;
  statusColorsEnabled?: boolean;
}

export function ResponseTableRow({ 
  response, 
  canManageRecruitment = false,
  canDelete = false,
  isSelected = false,
  onSelectionChange,
  statusColorsEnabled = false
}: ResponseTableRowProps) {
  const navigate = useNavigate();
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<RecruitmentStatus>(
    response.recruitment_status || 'pending'
  );
  
  
  const { updateRecruitmentStatus, isUpdating } = useRecruitmentStatus();
  const { deleteResponse, isDeleting } = useDeleteResponse(response.test_id);
  const { inviteToInterview, isInviting } = useInterviewInvitation();
  
  // Keep local state in sync with prop updates
  useEffect(() => {
    setCurrentStatus(response.recruitment_status || 'pending');
  }, [response.recruitment_status]);

  const totalMarks = response.test.questions?.reduce(
    (sum: number, q: any) => sum + (q.marks || 1),
    0
  ) || 0;

  const aiScore = response.total_ai_score || 0;

  const handleStatusChange = async (status: RecruitmentStatus) => {
    setCurrentStatus(status); // Update local state immediately
    try {
      await updateRecruitmentStatus.mutateAsync({
        responseId: response.id,
        status,
        notes: response.recruitment_notes || undefined,
      });
    } catch (error) {
      // Revert local state on error
      setCurrentStatus(response.recruitment_status || 'pending');
    }
  };

  const handleNavigate = () => {
    navigate(`/test-response/${response.id}`);
  };

  const handleOpenInNewTab = () => {
    window.open(`/test-response/${response.id}`, '_blank');
  };

  const handleOpenNotesDialog = () => {
    setNotesDialogOpen(true);
  };

  const handleOpenHistoryDrawer = () => {
    setHistoryDrawerOpen(true);
  };

  const handleDeleteResponse = async () => {
    try {
      await deleteResponse.mutateAsync(response.id);
      setDeleteDialogOpen(false);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handleInviteToInterview = async () => {
    try {
      await inviteToInterview.mutateAsync({
        responseIds: [response.id],
        testId: response.test_id
      });
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const canInviteToInterview = response.status === "completed" && 
    canManageRecruitment;

  // Get background color based on recruitment status
  const getRowBackgroundClass = () => {
    if (!statusColorsEnabled || response.status !== 'completed') {
      return 'hover:bg-muted/50';
    }
    
    if (currentStatus === 'shortlisted') {
      return 'bg-blue-50 dark:bg-blue-950/20 hover:bg-blue-100 dark:hover:bg-blue-950/30';
    }
    if (currentStatus === 'hired') {
      return 'bg-green-50 dark:bg-green-950/20 hover:bg-green-100 dark:hover:bg-green-950/30';
    }
    if (currentStatus === 'rejected') {
      return 'bg-red-50 dark:bg-red-950/20 hover:bg-red-100 dark:hover:bg-red-950/30';
    }
    return 'hover:bg-muted/50';
  };

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <TableRow className={`cursor-pointer ${getRowBackgroundClass()}`} onClick={handleNavigate}>
            {canManageRecruitment && (
              <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={onSelectionChange}
                />
              </TableCell>
            )}
            
            <TableCell className="font-medium">
              <div className="flex items-center space-x-3">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={response.candidate.avatar_url} />
                  <AvatarFallback>
                    {response.candidate.full_name?.charAt(0) || response.candidate.email.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-medium">{response.candidate.full_name}</div>
                </div>
              </div>
            </TableCell>
            <TableCell>
              <StatusDisplay status={response.status} />
            </TableCell>
            <TableCell>
              <div className="text-sm text-muted-foreground">
                {format(new Date(response.started_at), "MMM d, yyyy")}
              </div>
            </TableCell>
            <TableCell>
              {response.status === "completed" ? (
                <ScoreDisplay score={aiScore} total={totalMarks} />
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </TableCell>
            <TableCell>
              {response.status === "completed" ? (
                <IntegrityDisplay score={response.integrity_score || 0} />
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </TableCell>
            <TableCell onClick={(e) => e.stopPropagation()}>
              {response.status === "completed" && canManageRecruitment ? (
                <div className="flex items-center gap-2">
                  <RecruitmentStatusSelect
                    value={currentStatus}
                    onChange={handleStatusChange}
                    disabled={isUpdating}
                  />
                </div>
              ) : response.status === "completed" ? (
                <Badge variant="outline" className="capitalize">
                  {currentStatus}
                </Badge>
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </TableCell>
            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-end gap-1">
                {canInviteToInterview && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleInviteToInterview}
                    disabled={isInviting}
                    title={response.interview_invited_at ? "Re-send Interview Invitation" : "Invite to Interview"}
                  >
                    <Mail className="h-4 w-4" />
                  </Button>
                )}
                {response.status === "completed" && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleOpenHistoryDrawer}
                  >
                    <History className="h-4 w-4" />
                  </Button>
                )}
                {response.status === "completed" && canManageRecruitment && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleOpenNotesDialog}
                    disabled={isUpdating}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-48 bg-white dark:bg-slate-900 border shadow-md z-50">
          <ContextMenuItem onClick={handleOpenInNewTab}>Open in new tab</ContextMenuItem>
          {canDelete && response.status === "completed" && (
            <ContextMenuItem 
              onClick={() => setDeleteDialogOpen(true)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete response
            </ContextMenuItem>
          )}
        </ContextMenuContent>
      </ContextMenu>

      {response.status === "completed" && (
        <>
          <RecruitmentNotesDialog
            open={notesDialogOpen}
            onOpenChange={setNotesDialogOpen}
            response={response}
          />
          <ChangeHistoryDrawer
            open={historyDrawerOpen}
            onOpenChange={setHistoryDrawerOpen}
            response={response}
          />
          <DeleteResponseDialog
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
            onConfirm={handleDeleteResponse}
            isDeleting={isDeleting}
          />
        </>
      )}
    </>
  );
}
